import React from 'react';

function parseServerDateTime(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(raw)
    ? `${raw}Z`
    : raw;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(value) {
  if (!value) return '—';
  const date = parseServerDateTime(value);
  if (!date) return String(value);
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatDate(value) {
  if (!value) return '—';
  const date = parseServerDateTime(value);
  if (!date) return String(value);
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function formatMoney(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

export function formatDecimalInput(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
}

export function formatPlanLabel(plan) {
  const map = { free: 'Trial', basic: 'Essential', premium: 'Plus' };
  return map[plan] || 'Free';
}

export function formatPlanStatusLabel(status) {
  const map = { trial: 'Trial', active: 'Aktiv', canceled: 'Gekuendigt', expired: 'Abgelaufen' };
  return map[status] || 'Aktiv';
}

export function getPlanBadgeColor(plan) {
  if (plan === 'premium') return 'yellow';
  if (plan === 'basic') return 'cyan';
  return 'ghost';
}

export function formatMonitoringStatusLabel(status) {
  const map = { ok: 'OK', warn: 'Warnung', error: 'Kritisch' };
  return map[status] || status;
}

export function getPositionCurrencyLabel(index) {
  if (index === 'HDAX') return 'EUR';
  if (index === 'STOXX600') return 'EUR';
  if (index === 'SP500') return 'USD';
  if (index === 'NASDAQ100') return 'USD';
  return '';
}

const STOXX_TRADINGVIEW_SUFFIX_MAP = {
  '.AS': { exchange: 'EURONEXT' },
  '.BR': { exchange: 'EURONEXT' },
  '.CO': { exchange: 'OMXCOP', hyphenToUnderscore: true },
  '.DE': { exchange: 'XETR' },
  '.HE': { exchange: 'OMXHEX' },
  '.IR': { exchange: 'EURONEXTDUB' },
  '.L': { exchange: 'LSE' },
  '.LS': { exchange: 'EURONEXTLIS' },
  '.MC': { exchange: 'BME' },
  '.MI': { exchange: 'MIL' },
  '.OL': { exchange: 'OSL' },
  '.PA': { exchange: 'EURONEXT' },
  '.ST': { exchange: 'OMXSTO', hyphenToUnderscore: true },
  '.SW': { exchange: 'SIX' },
  '.VI': { exchange: 'VIE' },
  '.WA': { exchange: 'GPW' },
};

function stripSuffix(symbol, suffix) {
  return symbol.slice(0, -suffix.length);
}

function buildEuropeanTradingViewSymbol(raw, suffixMap) {
  const match = Object.entries(suffixMap)
    .sort((a, b) => b[0].length - a[0].length)
    .find(([suffix]) => raw.endsWith(suffix));
  if (!match) return '';
  const [suffix, config] = match;
  let base = stripSuffix(raw, suffix);
  if (config.hyphenToUnderscore) base = base.replace(/-/g, '_');
  return `${config.exchange}:${base}`;
}

export function getTradingViewSymbol(symbol, marketKey, tradingViewSymbol = '') {
  const mapped = String(tradingViewSymbol || '').trim();
  if (mapped) return mapped.toUpperCase();
  const raw = String(symbol || '').trim().toUpperCase();
  if (!raw) return '';
  if (marketKey === 'HDAX') return `XETR:${raw.replace(/\.DE$/, '')}`;
  if (marketKey === 'STOXX600') {
    const stoxxSymbol = buildEuropeanTradingViewSymbol(raw, STOXX_TRADINGVIEW_SUFFIX_MAP);
    if (stoxxSymbol) return stoxxSymbol;
  }
  if (marketKey === 'CUSTOM' && raw.endsWith('.DE')) return `XETR:${raw.replace(/\.DE$/, '')}`;
  return `BATS:${raw}`;
}

export function buildTradingViewUrl(symbol, marketKey, themeMode, tradingViewSymbol = '') {
  const tvSymbol = getTradingViewSymbol(symbol, marketKey, tradingViewSymbol);
  if (!tvSymbol) return '';
  if (isTradingViewEmbedRestrictedSymbol(tvSymbol)) return '';
  const config = {
    autosize: true,
    symbol: tvSymbol,
    interval: 'D',
    timezone: 'Europe/Berlin',
    theme: themeMode === 'light' ? 'light' : 'dark',
    style: '1',
    locale: 'de_DE',
    enable_publishing: false,
    hide_side_toolbar: false,
    allow_symbol_change: false,
    calendar: false,
    save_image: false,
    support_host: 'https://www.tradingview.com',
  };
  return `https://www.tradingview-widget.com/embed-widget/advanced-chart/?locale=de_DE#${encodeURIComponent(JSON.stringify(config))}`;
}

function isTradingViewEmbedRestrictedSymbol(tvSymbol) {
  const restrictedExchangePrefixes = [
    'EURONEXT',
    'EURONEXTDUB',
    'EURONEXTLIS',
    'LSE',
    'OSL',
  ];
  const normalized = String(tvSymbol || '').trim().toUpperCase();
  return restrictedExchangePrefixes.some(prefix => normalized.startsWith(`${prefix}:`));
}

function buildTradingViewExternalUrl(tvSymbol) {
  if (!tvSymbol) return '';
  return `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(tvSymbol)}`;
}

export function TradingViewChart({ symbol, marketKey, tradingViewSymbol, themeMode, COLORS }) {
  const tvSymbol = getTradingViewSymbol(symbol, marketKey, tradingViewSymbol);
  if (!tvSymbol) return null;
  const externalUrl = buildTradingViewExternalUrl(tvSymbol);
  if (isTradingViewEmbedRestrictedSymbol(tvSymbol)) {
    return (
      <div style={{
        border: `1px solid ${COLORS.border}`,
        borderRadius: '12px',
        background: themeMode === 'light' ? '#f7fbfd' : '#0b0d12',
        padding: '18px',
        minHeight: '150px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ color: COLORS.text, fontWeight: 800, marginBottom: '6px' }}>{tvSymbol}</div>
          <div style={{ color: COLORS.muted, fontSize: '13px' }}>
            TradingView erlaubt dieses Symbol nicht als eingebetteten Chart.
          </div>
        </div>
        <a
          href={externalUrl}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '36px',
            padding: '0 14px',
            borderRadius: '10px',
            background: COLORS.cyan,
            color: '#001014',
            fontWeight: 800,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          Bei TradingView öffnen
        </a>
      </div>
    );
  }
  const src = buildTradingViewUrl(symbol, marketKey, themeMode, tradingViewSymbol);
  if (!src) return null;
  return (
    <div style={{
      border: `1px solid ${COLORS.border}`,
      borderRadius: '12px',
      overflow: 'hidden',
      background: themeMode === 'light' ? '#f7fbfd' : '#0b0d12',
    }}>
      <iframe
        title={`TradingView ${symbol}`}
        src={src}
        style={{ width: '100%', height: '420px', border: 'none', display: 'block' }}
        loading="lazy"
      />
    </div>
  );
}
